import React from "react";
import TextBox from "../../Common/TextBox";
import SwitchButton from "../../Buttons/SwitchButton";
import "./preference.css";
import CommonDropDown from "../../Common/DropDown/CommonDropDown";

type DisplayValue = {
  displayName: string;
  name: string;
};

type TextBoxProps = {
  propertyPath: string;
  value?: string | number;
  onChange: (value: string | number) => void;
  variant: "text" | "number";
  isInteger?: boolean;
  disabled?: boolean;
  isUsingUnit?: boolean;
  onClickDelete?: () => void;
  isCanDelete?: boolean;
};

export function PreferenceItemTextBox({
  propertyPath,
  variant,
  value = variant === "number" ? 0 : "",
  onChange,
  isInteger = false,
  disabled = false,
  isUsingUnit = false,
  isCanDelete = false,
  onClickDelete = () => {},
}: TextBoxProps) {
  let className = variant === "number" ? "number" : "text";
  className += isCanDelete ? " removable" : "";
  return (
    <div>
      <TextBox
        className={className}
        name={propertyPath}
        value={value}
        onChange={(value) => onChange(value as string)}
        variant={variant}
        isInteger={isInteger}
        disabled={disabled}
        style={{
          height: 38,
          padding: "8px 14px 8px 14px",
          marginRight: !isUsingUnit ? (isCanDelete ? 12 : 48) : 0,
        }}
      />
      {isCanDelete && (
        <button className="property-delete-button" onClick={onClickDelete}>
          <svg
            width="13"
            height="12"
            viewBox="0 0 13 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.8324 12L0.5 10.6676L5.16759 6.00004L0.5 1.33245L1.8324 0L6.5 4.6676L11.1676 0L12.5 1.3324C10.6412 3.20757 9.69121 4.12483 7.8324 6L12.5 10.6676L11.1676 12L6.5 7.3324L1.8324 12Z"
              fill="#7B0000"
            />
            <path
              d="M1.8324 12L0.5 10.6676L5.16759 6.00004L0.5 1.33245L1.8324 0L6.5 4.6676L11.1676 0L12.5 1.3324C10.6412 3.20757 9.69121 4.12483 7.8324 6L12.5 10.6676L11.1676 12L6.5 7.3324L1.8324 12Z"
              fill="#7B0000"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

type SelectionProps = {
  value?: string;
  onChange: (value: string) => void;
  propertyPath: string;
  displayValues: DisplayValue[];
  disabled?: boolean;
  isCanDelete?: boolean;
  isUsingUnit?: boolean;
  onClickDelete?: () => void;
};

export function PreferenceItemSelection({
  value = "",
  onChange,
  propertyPath,
  displayValues,
  disabled = false,
  isCanDelete = false,
  isUsingUnit = false,
  onClickDelete = () => {},
}: SelectionProps) {
  const dropDownDisplayValue = displayValues.map((dv) => ({
    display: dv.displayName,
    value: dv.name,
    disabled: false,
  }));

  return (
    <div
      style={{
        marginRight: !isUsingUnit ? (isCanDelete ? 12 : 48) : 0,
      }}
    >
      <CommonDropDown
        id={`${propertyPath}-selector`}
        disabled={disabled}
        value={value}
        onChange={(value) => onChange(value)}
        dropDownDisplayValues={dropDownDisplayValue}
        style={{
          minWidth: 245,
          height: 38,
          fontSize: 16,
        }}
      />
      {isCanDelete && (
        <button className="property-delete-button" onClick={onClickDelete}>
          <svg
            width="13"
            height="12"
            viewBox="0 0 13 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.8324 12L0.5 10.6676L5.16759 6.00004L0.5 1.33245L1.8324 0L6.5 4.6676L11.1676 0L12.5 1.3324C10.6412 3.20757 9.69121 4.12483 7.8324 6L12.5 10.6676L11.1676 12L6.5 7.3324L1.8324 12Z"
              fill="#7B0000"
            />
            <path
              d="M1.8324 12L0.5 10.6676L5.16759 6.00004L0.5 1.33245L1.8324 0L6.5 4.6676L11.1676 0L12.5 1.3324C10.6412 3.20757 9.69121 4.12483 7.8324 6L12.5 10.6676L11.1676 12L6.5 7.3324L1.8324 12Z"
              fill="#7B0000"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

type ToggleProps = {
  value?: boolean;
  onChange: (value: boolean) => void;
  propertyPath: string;
  disabled?: boolean;
  isCanDelete?: boolean;
  isUsingUnit?: boolean;
  onClickDelete?: () => void;
};

export function PreferenceItemToggle({
  value = false,
  onChange,
  propertyPath,
  disabled = false,
  isCanDelete = false,
  isUsingUnit = false,
  onClickDelete = () => {},
}: ToggleProps) {
  return (
    <div
      style={{
        marginRight: !isUsingUnit ? (isCanDelete ? 12 : 48) : 0,
      }}
    >
      <SwitchButton
        isOn={value}
        onToggle={(isOn) => onChange(isOn)}
        withLetter={true}
        disabled={disabled}
      />
      {isCanDelete && (
        <button className="property-delete-button" onClick={onClickDelete}>
          <svg
            width="13"
            height="12"
            viewBox="0 0 13 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.8324 12L0.5 10.6676L5.16759 6.00004L0.5 1.33245L1.8324 0L6.5 4.6676L11.1676 0L12.5 1.3324C10.6412 3.20757 9.69121 4.12483 7.8324 6L12.5 10.6676L11.1676 12L6.5 7.3324L1.8324 12Z"
              fill="#7B0000"
            />
            <path
              d="M1.8324 12L0.5 10.6676L5.16759 6.00004L0.5 1.33245L1.8324 0L6.5 4.6676L11.1676 0L12.5 1.3324C10.6412 3.20757 9.69121 4.12483 7.8324 6L12.5 10.6676L11.1676 12L6.5 7.3324L1.8324 12Z"
              fill="#7B0000"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
